//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import {
  useIsMutating,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

import { shareModerationMedia } from '@apis/moderation/moderationApi';
import { ShareModerationMediaRequest } from '@apis/moderation/moderationApiModel';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { ALTERNATE_TOASTS } from '@legacy-components/common/toast-notifications/toastNotificationsConstants';
import { ToastType } from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import Cuid from '@models/Cuid';
import { QueryKeyConstants } from '@queries/QueryKeyConstants';
import { getNgRedux } from '@react-bridge/redux-bridge';

export const useShareMediaMutation = (cuid: Cuid) => {
  const queryClient = useQueryClient();

  const mutationKey = [
    QueryKeyConstants.UniversalImageActions,
    QueryKeyConstants.UniversalImageActionsShare,
    cuid,
  ];
  const mutationFilters = {
    mutationKey,
    exact: true,
  };
  const isSharePending = useIsMutating(mutationFilters) > 0;

  const mutation = useMutation({
    mutationKey,
    mutationFn: (request: ShareModerationMediaRequest) =>
      shareModerationMedia(request),
    onError: () => {
      getNgRedux().dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Share media',
          message: 'Failed to share this item. Please try again.',
          type: ToastType.ERROR,
          options: {
            toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
            extraData: {
              className: 'uia-share-toast',
            },
          },
        }),
      );
    },
    onSettled: async (_data, _error, request) => {
      await Promise.all([
        ...request.cuids.map((cuid) =>
          queryClient.invalidateQueries({
            queryKey: [
              QueryKeyConstants.UniversalImageActions,
              QueryKeyConstants.UniversalImageActionsRights,
              QueryKeyConstants.UniversalImageActionsMetadata,
              cuid,
            ],
          }),
        ),
        queryClient.invalidateQueries({
          queryKey: [
            QueryKeyConstants.MediaFilters,
            QueryKeyConstants.MediaFiltersMediaItems,
          ],
        }),
      ]);
    },
    onSuccess: () => {
      getNgRedux().dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Success!',
          message: 'Item shared successfully.',
          type: ToastType.SUCCESS,
          options: {
            toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
            extraData: {
              className: 'uia-share-toast',
            },
          },
        }),
      );
    },
  });

  return {
    ...mutation,
    hasPendingShare: () =>
      queryClient.isMutating(mutationFilters) > 0,
    isPending: mutation.isPending || isSharePending,
  };
};
